import type { Building } from "building-types/building/buildingTypes";
import { WallLateralDatumAnchorAll } from "building-types/datums/datumsTypes";
import { DoorOperationType } from "building-types/doors/doorOperationTypes";
import type { DoorUnit } from "building-types/doors/doorTypes";
import { SwingSide } from "building-types/doors/doorTypes";
import { hardCodedAutoPlacedPropLabels } from "building-types/entities/labelTypes";
import type { Prop } from "building-types/props/propTypes";
import type { ActiveProfileSource } from "building-types/settings/profileCatalogSettings";
import { AwningType, PropCatalogType } from "building-types/settings/settingsTypesEnum";
import { DoorViewNoteType } from "building-types/settings/viewNotesSettings";
import { ViewEntityType } from "building-types/views/viewEntityType";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { RWTable, Table } from "common/data/table";
import { radianToDegree } from "common/utils/math";
import uid from "common/utils/uid";
import { getDoorHomeSettings } from "doc/elements/doors/doorHomeSettings";
import getMergedDoorSettings from "doc/elements/doors/getMergedDoorSettings";
import { getMergeSettingsForOpeningParamsFromBuilding } from "doc/mergeSettingsSet";
import { mergedPropLibraryNew, propNew } from "doc/propTools";
import { DOOR_TRIM_WIDTH } from "doc/utils/doorTools";
import { viewEntityDescriptorNew } from "doc/utils/viewEntityTools";
import { planarGraphAdjacentHalfEdge, planarGraphHalfEdgeLength } from "geom/planarGraph";
import { planeNormal } from "geom/plane";
import { polyLine2Copy, polyLine2Extend, polyLine2Move, polyLine2Reverse } from "geom/polyLine2";
import { polyLine3Copy, polyLine3Move } from "geom/polyLine3";
import { getLine } from "geom/rect2";
import { vec2AngleBetween } from "geom/vec2";
import { addAwning } from "../elements/awnings";
import { computeDoorSwingSide } from "../elements/computeDoorSwingSide";
import { doorBackOpening3, doorFrontOpening3 } from "../elements/doors/doorQuery";
import { doorUnitArc, doorUnitSimplePanel, doorUnitWithProp } from "../elements/doors/doorUnit";
import { entityLateralOffsetFromWallDatum } from "../elements/openings/openingWallLayers";
import { wallThickness } from "../elements/walls/wall";
import { inchesToFeetInches, propPlacementSwingAdjust } from "../generationTools/generateDoorUnitsTools";
import { isCombinedGlobalInstanceMirror } from "../generationTools/generateOpeningsTools";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import { memoAsync } from "../utils/memo";

function select({
  doorPlacements: doors,
  settings,
  planarGraphs,
  planarGraphPerimeters,
  walls,
  levels,
  mirror,
  roomOutlineTable,
  wallMasses,
  optionChoices,
  doc,
  settingsSets,
  enabledFeatures,
}: Pick<
  Building,
  | "doorPlacements"
  | "settings"
  | "planarGraphs"
  | "planarGraphPerimeters"
  | "walls"
  | "levels"
  | "mirror"
  | "roomOutlineTable"
  | "wallMasses"
  | "optionChoices"
  | "doc"
  | "settingsSets"
  | "enabledFeatures"
>) {
  return {
    doors,
    planarGraphs,
    walls,
    levels,
    roomOutlineTable,
    wallMasses,
    planarGraphPerimeters,
    propCatalog: settings.props,
    assets: doc.assets,
    profileSettings: settings.profile,
    profiles: doc.profiles,
    settingsToCatalogs: enabledFeatures.settingsToCatalogs,
    mirror,
    mergeSettingsForOpeningParams: getMergeSettingsForOpeningParamsFromBuilding(
      { settingsSets, optionChoices, settings },
      doc,
    ),
    doorHomeSettings: getDoorHomeSettings(settings),
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  mergeSettingsForOpeningParams: "deep",
  doorHomeSettings: "shallow",
  assets: "shallow",
});

async function generate({
  doors,
  planarGraphs,
  walls,
  levels,
  roomOutlineTable,
  wallMasses,
  planarGraphPerimeters,
  propCatalog,
  assets,
  profileSettings,
  profiles,
  settingsToCatalogs,
  mirror,
  mergeSettingsForOpeningParams,
  doorHomeSettings,
}: ReturnType<typeof select>): Promise<{ doorUnits: Table<DoorUnit> }> {
  const doorUnits: RWTable<DoorUnit> = {};
  const mergedPropLibrary = mergedPropLibraryNew(assets);
  const profileSource: ActiveProfileSource = { settingsToCatalogs, profiles, profileSettings };

  /**
   * TODO:-move this block to generateDoorUnitsTools
   */
  for (const doorId in doors) {
    const door = doors[doorId];

    if (!door.opening) {
      continue;
    }

    // if door operation type is open then prop is not needed
    const propNeeded = !(door.descriptor.operationType === DoorOperationType.OPEN);

    if (!door.frontRoom) {
      console.assert(false, "door is missing front room");
      continue;
    }

    const { entitySettings, activeSettingsSets, settings, optionChoices } = mergeSettingsForOpeningParams;

    const mergedDoorSettings = getMergedDoorSettings({
      homeSettings: doorHomeSettings,
      door,
      descriptor: door.descriptor,
      entitySettings,
      activeSettingsSets,
      settings,
      optionChoices,
    });

    const viewEntityDescriptor = viewEntityDescriptorNew(
      uid(),
      ViewEntityType.DOOR,
      mergedDoorSettings.viewNoteTable[DoorViewNoteType.DOOR],
    );

    const levelId = door.frontRoom.level.id;

    const perimeter = planarGraphPerimeters[levelId];
    const streetOrientation =
      perimeter.coveredPerimeterStreetOrientations[door.halfEdgeIndex] ??
      perimeter.coveredPerimeterStreetOrientations[planarGraphAdjacentHalfEdge(door.halfEdgeIndex)];

    const { floorElevation } = levels[levelId];

    const wallIndex = walls[levelId].wallIndex;
    const wallId = wallIndex.halfEdgeToWall[door.halfEdgeIndex];
    const wall = wallIndex.walls[wallId];
    const wallThicknessComputed = wallThickness({ wall, wallLayer: WallLayerType.SUBSTRATE });

    const frontPlane = door.reverse ? door.opening.backPlane : door.opening.frontPlane;
    const [wallNormX, wallNormY] = planeNormal(frontPlane);

    const rotation = radianToDegree(vec2AngleBetween(0, 1, wallNormX, wallNormY));

    const lateralOffsetX = -(wallNormX * door.lateralOffset);
    const lateralOffsetY = -(wallNormY * door.lateralOffset);

    const opening3 = door.reverse ? doorBackOpening3(door, false) : doorFrontOpening3(door, false);
    if (!opening3) {
      continue;
    }

    const hostOutline = polyLine3Copy(opening3);
    polyLine3Move(hostOutline, lateralOffsetX, lateralOffsetY, 0);

    const x = hostOutline[3];
    const y = hostOutline[4];

    let unit: DoorUnit;

    const [heightFeet, heightInches] = inchesToFeetInches(door.descriptor.height - door.descriptor.zOffset);
    const [widthFeet, widthInches] = inchesToFeetInches(door.descriptor.width);

    const doorX = x;
    const doorY = y;

    let swingSide: SwingSide = SwingSide.NONE;
    let prop: Prop | undefined = undefined;

    if (propNeeded) {
      swingSide = computeDoorSwingSide(door, wallIndex);
      if (!door.swingSide && door.descriptor.swingSide) {
        swingSide = door.descriptor.swingSide;
      }

      let propId: string | undefined = undefined;
      if (door.descriptor.propId) {
        propId = door.descriptor.propId;
      } else if (door.descriptor.propIdPrefix) {
        propId =
          door.descriptor.propIdPrefix +
          widthFeet.toFixed(0) +
          widthInches.toFixed(0) +
          heightFeet.toFixed(0) +
          heightInches.toFixed(0);
      }

      if (propId) {
        const catalogType = PropCatalogType.PROP_CATALOG;
        const propAttributes = propCatalog[propId];
        if (propAttributes) {
          //adjustments for prop placement
          const [adjustX, adjustY] = await propPlacementSwingAdjust(
            door.descriptor,
            propId,
            swingSide,
            wallNormX,
            wallNormY,
            propCatalog,
            mergedPropLibrary,
          );

          let propX: number = doorX + adjustX;
          let propY: number = doorY + adjustY;

          if (door.descriptor.operationType === DoorOperationType.WALL_MOUNTED) {
            propX = propX + wallThicknessComputed * wallNormX;
            propY = propY + wallThicknessComputed * wallNormY;
          }

          prop = propNew({
            room: door.frontRoom,
            src: propId,
            x: propX,
            y: propY,
            z: floorElevation + door.descriptor.zOffset + door.verticalOffset,
            rotationDegrees: rotation,
            descriptor: viewEntityDescriptor,
            catalogType,
            isExterior: door.descriptor.isExterior,
            labels: hardCodedAutoPlacedPropLabels, // We could consider making this configurable.
          });
        }
      }
    }

    if (
      door.descriptor.operationType === DoorOperationType.ARC ||
      door.descriptor.operationType === DoorOperationType.PORTICO
    ) {
      unit = doorUnitArc({
        door,
        streetOrientation,
        wallIndex,
        swingSide,
        wallThickness: wallThicknessComputed,
        panelOffsetDepth: door.descriptor.panelOffsetDepth,
        prop: prop,
        mergedDoorSettings,
      });
    } else if (prop) {
      unit = doorUnitWithProp({
        door,
        swingSide,
        prop,
        streetOrientation,
        mergedDoorSettings,
        profileSource,
      });
    } else {
      unit = doorUnitSimplePanel({
        door,
        swingSide,
        panelThickness: door.descriptor.panelThickness,
        panelOffsetDepth: door.descriptor.panelOffsetDepth,
        prop,
        streetOrientation: streetOrientation,
        mergedDoorSettings,
        viewEntityDescriptor,
        profileSource,
      });
    }

    if (!unit) {
      continue;
    }

    doorUnits[doorId] = unit;

    if (mergedDoorSettings.awningSettings?.awningType !== AwningType.DISABLED) {
      const wallLength = planarGraphHalfEdgeLength(
        planarGraphs[door.frontRoom.level.id].planarGraph,
        door.halfEdgeIndex,
      );
      const outline = door.opening.back.opening2;
      const awningBottomLine = polyLine2Reverse(polyLine2Copy(getLine(outline, 2)));
      polyLine2Extend(awningBottomLine, DOOR_TRIM_WIDTH, DOOR_TRIM_WIDTH);
      polyLine2Move(awningBottomLine, 0, DOOR_TRIM_WIDTH + unit.headCasingHeight);

      if (door.descriptor.isExterior && roomOutlineTable && wallMasses) {
        const roomOutlines = roomOutlineTable[door.frontRoom.id];
        const wallMassSystem = wallMasses[door.frontRoom.level.id];

        let resolvedLateralOffset = mergedDoorSettings?.awningSettings?.awningLateralOffset ?? 0;
        const edgeIndex = door.frontRoom?.edgeIds.indexOf(door.edgeId);
        if (roomOutlines && wallMassSystem && edgeIndex > -1) {
          resolvedLateralOffset = entityLateralOffsetFromWallDatum({
            room: door.frontRoom,
            edgeIndex,
            roomOutlines,
            wallMassSystem,
            placement: {
              lateralDatum:
                mergedDoorSettings?.awningSettings?.awningLateralDatum ?? WallLateralDatumAnchorAll.ROOM_EDGE,
              lateralOffsetNormalized: false,
              lateralOffset: resolvedLateralOffset,
            },
            resolvedParam: 1,
          });
        }

        await addAwning(
          door.awning,
          door.frontRoom,
          door.backRoom,
          resolvedLateralOffset,
          wallLength,
          mergedDoorSettings.awningSettings,
          propCatalog,
          assets,
          door.opening.backPlane,
          awningBottomLine,
          unit.decorations,
          unit.awningRoof,
          unit.props,
          mergedDoorSettings.viewNoteTable,
          isCombinedGlobalInstanceMirror(mirror, door.frontRoom ?? door.backRoom),
        );
      }
    }
  }

  return { doorUnits };
}

export default memoAsync(select, generate, compare);
